import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { APP_NAME, SLOGAN, LOGO_T, applyTheme } from "@/lib/brand";
import { PLANS } from "@/lib/plans";
import { Button } from "@/components/ui/button";
import {
  TrendingUp, Package, Users, BarChart3, FileText, Boxes, Check, ArrowRight, ClipboardList, Smartphone, UserPlus,
} from "lucide-react";
import { t } from "@/lib/i18n";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";

const FEATURES = [
  { icon: ClipboardList, title: t("Ordini in pochi tap"), desc: t("I tuoi agenti raccolgono ordini ovunque, dal cliente al magazzino, senza carta.") },
  { icon: Package, title: t("Catalogo e scorte"), desc: t("Carica i prodotti (anche da file), gestisci giacenze e ricevi avvisi di scorta bassa.") },
  { icon: Users, title: t("Rete agenti"), desc: t("Invita i tuoi agenti, assegna provvigioni e monitora le performance di ciascuno.") },
  { icon: BarChart3, title: t("Statistiche chiare"), desc: t("Fatturato, ordini, classifiche agenti e prodotti top, con filtro per periodo.") },
  { icon: FileText, title: t("PDF d'ordine"), desc: t("Genera e invia al volo il PDF dell'ordine, pronto per il cliente.") },
  { icon: Boxes, title: t("Listini per cliente"), desc: t("Prezzi e sconti dedicati per ogni cliente, applicati in automatico in fase d'ordine.") },
];

const STEPS = [
  { n: "01", t: t("Crea l'azienda"), d: t("Registrati e inserisci il catalogo prodotti in pochi minuti.") },
  { n: "02", t: t("Invita gli agenti"), d: t("Condividi il codice: i tuoi agenti accedono gratis alla rete.") },
  { n: "03", t: t("Raccogli ordini"), d: t("Gli agenti ordinano per i clienti, tu vedi tutto in tempo reale.") },
  { n: "04", t: t("Analizza e cresci"), d: t("Statistiche e provvigioni sempre sotto controllo.") },
];

const DarkBg = () => (
  <div aria-hidden className="absolute inset-0 overflow-hidden pointer-events-none">
    <div className="absolute inset-0 bg-[radial-gradient(#1e293b_1px,transparent_1px)] [background-size:24px_24px] opacity-70" />
    <div className="absolute -top-40 -left-32 h-[520px] w-[520px] rounded-full bg-blue-600/25 blur-[120px]" />
    <div className="absolute -top-20 right-0 h-[440px] w-[440px] rounded-full bg-emerald-500/20 blur-[120px]" />
    <div className="absolute inset-0 ordex-diagonals" />
    <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-emerald-400/50 to-transparent" />
  </div>
);

function Nav({ navigate }) {
  return (
    <header className="sticky top-0 z-40 bg-[#0B0F19]/80 backdrop-blur-xl border-b border-white/10">
      <div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <img src={LOGO_T} alt={APP_NAME} className="h-9 w-9" />
          <span className="hidden sm:inline font-display text-lg font-extrabold tracking-tight text-white">{APP_NAME}</span>
        </div>
        <div className="flex items-center gap-1.5 sm:gap-2">
          <a href="#prezzi" className="hidden sm:inline text-sm text-slate-300 hover:text-white px-3 transition-colors" data-testid="landing-pricing-link">{t("Prezzi")}</a>
          <LanguageSwitcher dark />
          <Button variant="ghost" onClick={() => navigate("/login")} className="hidden min-[400px]:inline-flex text-slate-200 hover:text-white hover:bg-white/10 px-2 sm:px-4" data-testid="landing-login-btn">{t("Accedi")}</Button>
          <Button onClick={() => navigate("/login?mode=register")} className="gap-2 font-semibold bg-emerald-500 hover:bg-emerald-400 text-slate-950 px-3 sm:px-4" data-testid="landing-register-btn">
            {t("Inizia gratis")} <ArrowRight className="hidden sm:block h-4 w-4" />
          </Button>
        </div>
      </div>
    </header>
  );
}

function HeroMock() {
  return (
    <motion.div initial={{ opacity: 0, y: 24, rotate: -1 }} animate={{ opacity: 1, y: 0, rotate: 0 }} transition={{ duration: 0.6, delay: 0.15 }} className="relative">
      <div className="absolute -inset-1 rounded-3xl bg-gradient-to-br from-blue-500/60 via-transparent to-emerald-400/60 blur-sm" />
      <div className="relative rounded-3xl border border-white/10 bg-[#111827]/90 backdrop-blur p-6 shadow-2xl">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <img src={LOGO_T} alt="" className="h-7 w-7" />
            <span className="font-display font-bold text-white">{t("Dashboard")}</span>
          </div>
          <span className="text-[10px] font-mono-j uppercase tracking-[0.2em] text-emerald-400">{t("● live")}</span>
        </div>
        <div className="grid grid-cols-3 gap-3">
          {[[t("Vendite"), "€ 48.250"], [t("Ordini"), "132"], [t("Agenti"), "7"]].map(([k, v]) => (
            <div key={k} className="rounded-xl bg-white/5 border border-white/10 p-3">
              <p className="text-[10px] uppercase tracking-wider text-slate-400">{k}</p>
              <p className="font-mono-j text-lg font-semibold text-white mt-1">{v}</p>
            </div>
          ))}
        </div>
        <div className="mt-4 h-32 rounded-xl bg-gradient-to-t from-emerald-500/10 to-transparent border border-white/5 flex items-end gap-1.5 p-3">
          {[30, 45, 38, 60, 52, 75, 68, 90].map((h, i) => (
            <motion.div key={h} initial={{ height: 0 }} animate={{ height: `${h}%` }} transition={{ delay: 0.4 + i * 0.06, duration: 0.5 }}
              className="flex-1 rounded-t bg-gradient-to-t from-blue-600 to-emerald-400" />
          ))}
        </div>
      </div>
      <div className="absolute -bottom-5 -left-5 bg-white rounded-xl shadow-xl p-3 flex items-center gap-2">
        <Smartphone className="h-5 w-5 text-blue-600" />
        <span className="text-sm font-semibold text-slate-900">{t("Ordini anche da mobile")}</span>
      </div>
    </motion.div>
  );
}

function Hero({ navigate }) {
  return (
    <section className="relative bg-[#0B0F19] text-white">
      <DarkBg />
      <div className="relative max-w-6xl mx-auto px-5 pt-20 pb-24 grid lg:grid-cols-2 gap-14 items-center">
        <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <span className="inline-flex items-center gap-2 text-[11px] font-mono-j uppercase tracking-[0.2em] text-emerald-400 border border-emerald-400/30 bg-emerald-400/10 px-3 py-1.5 rounded-full">
            <TrendingUp className="h-3.5 w-3.5" /> {t("Gestionale vendite")}
          </span>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mt-6 leading-[1.02]">
            {t("Più ordini,")}<br />
            <span className="bg-gradient-to-r from-blue-400 via-sky-300 to-emerald-400 bg-clip-text text-transparent">{t("più crescita.")}</span>
          </h1>
          <p className="text-base sm:text-lg text-slate-300 mt-6 max-w-md leading-relaxed">
            {APP_NAME} {t("è il gestionale che collega aziende e agenti di commercio: prendi ordini, gestisci il catalogo e leggi le statistiche di vendita, tutto in un posto solo.")}
          </p>
          <div className="flex flex-wrap gap-3 mt-8">
            <Button size="lg" onClick={() => navigate("/login?mode=register")} className="gap-2 font-semibold h-12 px-6 bg-emerald-500 hover:bg-emerald-400 text-slate-950 shadow-[0_0_30px_rgba(16,185,129,0.35)]" data-testid="hero-cta-btn">
              {t("Prova 14 giorni gratis")} <ArrowRight className="h-4 w-4" />
            </Button>
            <Button size="lg" variant="outline" onClick={() => navigate("/login")} className="h-12 px-6 bg-transparent border-white/20 text-white hover:bg-white/10 hover:text-white" data-testid="hero-login-btn">
              {t("Ho già un account")}
            </Button>
          </div>
          <p className="text-xs text-slate-400 mt-4">{t("Nessuna carta richiesta per la prova. Gli agenti invitati usano l'app gratis.")}</p>
        </motion.div>
        <HeroMock />
      </div>
    </section>
  );
}

function Features() {
  return (
    <section className="max-w-6xl mx-auto px-5 py-20">
      <div className="max-w-2xl">
        <p className="text-[11px] font-mono-j uppercase tracking-[0.2em] text-blue-600">{t("Perché")} {APP_NAME}</p>
        <h2 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight mt-3">{t("Perché la tua azienda ne ha bisogno")}</h2>
        <p className="text-slate-600 mt-4">
          {t("Basta ordini presi su carta o WhatsApp.")} {APP_NAME} {t("digitalizza la raccolta ordini e ti dà il controllo completo su prodotti, agenti e fatturato.")}
        </p>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200 rounded-2xl overflow-hidden mt-12">
        {FEATURES.map((f, i) => (
          <motion.div key={f.title} initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}
            className="group bg-white p-7 relative hover:bg-slate-50 transition-colors">
            <span className="absolute left-0 top-0 h-0.5 w-0 bg-gradient-to-r from-blue-600 to-emerald-500 group-hover:w-full transition-[width] duration-500" />
            <div className="h-11 w-11 rounded-lg bg-slate-900 grid place-items-center mb-5">
              <f.icon className="h-5 w-5 text-emerald-400" />
            </div>
            <h3 className="font-semibold text-lg text-slate-900">{f.title}</h3>
            <p className="text-sm text-slate-600 mt-2 leading-relaxed">{f.desc}</p>
          </motion.div>
        ))}
      </div>
    </section>
  );
}

function Steps() {
  return (
    <section className="relative bg-[#0B0F19] text-white overflow-hidden">
      <DarkBg />
      <div className="relative max-w-6xl mx-auto px-5 py-20">
        <h2 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">{t("Come funziona")}</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-12">
          {STEPS.map((s) => (
            <div key={s.n} className="rounded-2xl bg-white/[0.04] border border-white/10 p-6 hover:border-emerald-400/40 transition-colors">
              <span className="font-mono-j text-3xl font-semibold bg-gradient-to-r from-blue-400 to-emerald-400 bg-clip-text text-transparent">{s.n}</span>
              <h3 className="font-semibold mt-4">{s.t}</h3>
              <p className="text-sm text-slate-400 mt-1.5">{s.d}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function PlanCard({ p, i, navigate }) {
  return (
    <motion.div initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
      className="relative rounded-2xl bg-white border border-slate-200 p-7 flex flex-col hover:-translate-y-1 hover:shadow-xl transition-[transform,box-shadow] duration-300"
      data-testid={`landing-plan-${p.key}`}>
      <span className="absolute top-0 left-6 right-6 h-1 rounded-b-full" style={{ background: p.color }} />
      <div className="flex items-center justify-between">
        <p className={`font-display text-xl font-extrabold ${p.text}`}>{p.name}</p>
        <span className="text-[10px] font-mono-j uppercase tracking-wider px-2 py-1 rounded-full" style={{ background: `${p.color}15`, color: p.color }}>{p.tag}</span>
      </div>
      <p className="mt-4"><span className="font-display text-5xl font-extrabold text-slate-900">{p.price}€</span><span className="text-slate-500">{t("/mese")}</span></p>
      <ul className="mt-6 space-y-2.5 text-sm text-slate-700 flex-1">
        {p.features.map((f) => (
          <li key={f} className="flex items-center gap-2"><Check className="h-4 w-4 shrink-0" style={{ color: p.color }} /> {f}</li>
        ))}
      </ul>
      <Button className={`w-full mt-7 h-11 font-semibold text-white ${p.btn}`} onClick={() => navigate("/login?mode=register")} data-testid={`landing-plan-${p.key}-btn`}>
        {t("Prova")} {p.name} {t("14 giorni gratis")}
      </Button>
    </motion.div>
  );
}

function Pricing({ navigate }) {
  return (
    <section id="prezzi" className="relative bg-slate-50 border-y border-slate-200">
      <div className="max-w-6xl mx-auto px-5 py-20">
        <p className="text-[11px] font-mono-j uppercase tracking-[0.2em] text-blue-600 text-center">{t("Prezzi")}</p>
        <h2 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-center mt-3">{t("Un piano per ogni rete vendita")}</h2>
        <p className="text-slate-600 text-center mt-3">{t("Tutti i piani includono 14 giorni di prova gratuita. Disdici quando vuoi.")}</p>
        <div className="grid md:grid-cols-3 gap-6 mt-12">
          {PLANS.map((p, i) => <PlanCard key={p.key} p={p} i={i} navigate={navigate} />)}
        </div>
        <div className="mt-8 rounded-2xl bg-[#0B0F19] text-white p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4" data-testid="landing-agent-banner">
          <div className="flex items-center gap-4">
            <div className="h-11 w-11 rounded-xl bg-emerald-500/15 grid place-items-center"><UserPlus className="h-5 w-5 text-emerald-400" /></div>
            <div>
              <p className="font-semibold">{t("Agenti? L'app per voi è gratis.")}</p>
              <p className="text-sm text-slate-400">{t("Invito dall'azienda, più aziende collegate, raccolta ordini mobile e statistiche provvigioni.")}</p>
            </div>
          </div>
          <Button variant="outline" onClick={() => navigate("/login")} className="bg-transparent border-white/20 text-white hover:bg-white/10 hover:text-white" data-testid="pricing-agent-btn">{t("Accedi come agente")}</Button>
        </div>
      </div>
    </section>
  );
}

export default function Landing() {
  const navigate = useNavigate();
  useEffect(() => { applyTheme("blue"); }, []);
  return (
    <div className="min-h-screen bg-white text-slate-900 overflow-x-hidden">
      <Nav navigate={navigate} />
      <Hero navigate={navigate} />
      <Features />
      <Steps />
      <Pricing navigate={navigate} />
      <footer className="bg-[#0B0F19] text-white">
        <div className="max-w-6xl mx-auto px-5 py-8 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <img src={LOGO_T} alt="" className="h-7 w-7" />
            <span className="font-display font-bold">{APP_NAME}</span>
            <span className="text-[10px] font-mono-j uppercase tracking-[0.2em] text-emerald-400 ml-2">{SLOGAN}</span>
          </div>
          <p className="text-xs text-slate-500">© 2026 {APP_NAME}</p>
        </div>
      </footer>
    </div>
  );
}
